import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  StarOff,
  Search,
  Filter,
  MoreHorizontal,
  ArrowLeft,
  Save,
  X
} from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

const AdminSubcategories = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    count: '',
    price: '',
    description: '',
    is_active: true,
    is_featured: false
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.PROJECT_CATEGORIES);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCategories(data.data);
        if (data.data.length > 0) {
          setSelectedCategory(data.data[0]);
          setSubcategories(data.data[0].subcategories || []);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSubcategories(category.subcategories || []);
    setSearchTerm('');
    setFilterStatus('all');
  };

  const handleAddSubcategory = () => {
    setEditingSubcategory(null);
    setFormData({
      name: '',
      count: '',
      price: '',
      description: '',
      is_active: true,
      is_featured: false
    });
    setShowModal(true);
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      count: subcategory.count,
      price: subcategory.price,
      description: subcategory.description || '',
      is_active: subcategory.is_active !== false,
      is_featured: subcategory.is_featured || false
    });
    setShowModal(true);
  };

  const handleSaveSubcategory = async () => {
    try {
      const updatedSubcategories = [...subcategories];
      
      if (editingSubcategory) {
        // Update existing subcategory
        const index = updatedSubcategories.findIndex(sub => sub.name === editingSubcategory.name);
        if (index !== -1) {
          updatedSubcategories[index] = { ...formData };
        }
      } else {
        // Add new subcategory
        updatedSubcategories.push({ ...formData });
      }

      // Update the category with new subcategories
      const updatedCategories = categories.map(cat => 
        cat.id === selectedCategory.id 
          ? { ...cat, subcategories: updatedSubcategories }
          : cat
      );

      setCategories(updatedCategories);
      setSubcategories(updatedSubcategories);
      setSelectedCategory({ ...selectedCategory, subcategories: updatedSubcategories });
      
      // Here you would typically save to API
      // await saveSubcategories(selectedCategory.id, updatedSubcategories);
      
      setShowModal(false);
      setFormData({
        name: '',
        count: '',
        price: '',
        description: '',
        is_active: true,
        is_featured: false
      });
    } catch (error) {
      console.error('Error saving subcategory:', error);
    }
  };

  const handleDeleteSubcategory = (subcategoryName) => {
    if (window.confirm('Bu alt kategoriyi silmek istediğinizden emin misiniz?')) {
      const updatedSubcategories = subcategories.filter(sub => sub.name !== subcategoryName);
      
      const updatedCategories = categories.map(cat => 
        cat.id === selectedCategory.id 
          ? { ...cat, subcategories: updatedSubcategories }
          : cat
      );

      setCategories(updatedCategories);
      setSubcategories(updatedSubcategories);
      setSelectedCategory({ ...selectedCategory, subcategories: updatedSubcategories });
    }
  };

  const handleToggleStatus = (subcategoryName) => {
    const updatedSubcategories = subcategories.map(sub => 
      sub.name === subcategoryName 
        ? { ...sub, is_active: !sub.is_active }
        : sub
    );
    
    const updatedCategories = categories.map(cat => 
      cat.id === selectedCategory.id 
        ? { ...cat, subcategories: updatedSubcategories }
        : cat
    );

    setCategories(updatedCategories);
    setSubcategories(updatedSubcategories);
    setSelectedCategory({ ...selectedCategory, subcategories: updatedSubcategories });
  };

  const handleToggleFeatured = (subcategoryName) => {
    const updatedSubcategories = subcategories.map(sub => 
      sub.name === subcategoryName 
        ? { ...sub, is_featured: !sub.is_featured }
        : sub
    );
    
    const updatedCategories = categories.map(cat => 
      cat.id === selectedCategory.id 
        ? { ...cat, subcategories: updatedSubcategories }
        : cat
    );

    setCategories(updatedCategories);
    setSubcategories(updatedSubcategories);
    setSelectedCategory({ ...selectedCategory, subcategories: updatedSubcategories });
  };

  const filteredSubcategories = subcategories.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && sub.is_active !== false) ||
      (filterStatus === 'inactive' && sub.is_active === false) ||
      (filterStatus === 'featured' && sub.is_featured);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Alt Kategoriler</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">Proje kategorilerinin alt kategorilerini yönetin</p>
        </div>
        <Button onClick={handleAddSubcategory} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm sm:text-base">
          <Plus className="w-4 h-4 mr-2" />
          Alt Kategori Ekle
        </Button>
      </div>

      {/* Category Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Kategori Seçin</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => handleCategoryChange(category)}
                className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedCategory?.id === category.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{category.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {category.subcategories?.length || 0} alt kategori
                    </p>
                  </div>
                  <Badge variant={category.is_active ? 'default' : 'secondary'} className="ml-2 flex-shrink-0 text-xs">
                    {category.is_active ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subcategories List */}
      {selectedCategory && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <CardTitle className="text-base sm:text-lg truncate">
                {selectedCategory.name} - Alt Kategoriler
              </CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Alt kategori ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tümü</option>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                  <option value="featured">Öne Çıkan</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              {filteredSubcategories.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-xs sm:text-sm text-gray-500">
                  {searchTerm || filterStatus !== 'all' 
                    ? 'Arama kriterlerinize uygun alt kategori bulunamadı.'
                    : 'Bu kategoride henüz alt kategori bulunmuyor.'
                  }
                </div>
              ) : (
                filteredSubcategories.map((subcategory, index) => (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 w-full min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{subcategory.name}</h3>
                        {subcategory.is_featured && (
                          <Badge variant="default" className="bg-yellow-100 text-yellow-800 text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Öne Çıkan
                          </Badge>
                        )}
                        <Badge variant={subcategory.is_active !== false ? 'default' : 'secondary'} className="text-xs">
                          {subcategory.is_active !== false ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <span>Proje Sayısı: {subcategory.count}</span>
                        <span>Fiyat: {subcategory.price}</span>
                      </div>
                      {subcategory.description && (
                        <p className="mt-1 text-xs sm:text-sm text-gray-500 line-clamp-2">{subcategory.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(subcategory.name)}
                        className={`${subcategory.is_active !== false ? 'text-green-600' : 'text-red-600'} p-1.5 sm:p-1`}
                      >
                        {subcategory.is_active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleFeatured(subcategory.name)}
                        className={`${subcategory.is_featured ? 'text-yellow-600' : 'text-gray-600'} p-1.5 sm:p-1`}
                      >
                        {subcategory.is_featured ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSubcategory(subcategory)}
                        className="p-1.5 sm:p-1"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSubcategory(subcategory.name)}
                        className="text-red-600 hover:text-red-700 p-1.5 sm:p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-xl font-semibold">
                {editingSubcategory ? 'Alt Kategori Düzenle' : 'Yeni Alt Kategori'}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Alt Kategori Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: Moda Mağazası"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Proje Sayısı
                </label>
                <input
                  type="text"
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: 8 Proje"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Fiyat Aralığı
                </label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: ₺15.000-50.000"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Alt kategori açıklaması..."
                />
              </div>
              
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Aktif</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Öne Çıkan</span>
                </label>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto text-sm sm:text-base"
              >
                İptal
              </Button>
              <Button
                onClick={handleSaveSubcategory}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm sm:text-base"
              >
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubcategories;
